import { useEffect, useRef, useCallback } from 'react';
import { WebSocketManager } from '../services/api';
import { useWorkflowStore } from '../stores/workflowStore';
import { WebSocketMessage } from '../types/workflow';

interface UseWebSocketOptions {
  onMessage?: (data: any) => void;
  onError?: (error: Event) => void;
  autoConnect?: boolean;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { onMessage, onError, autoConnect = true } = options;
  const wsManager = useRef<WebSocketManager | null>(null);
  const { setExecutionProgress, setCurrentStep, setCurrentExecution } = useWorkflowStore();

  const handleMessage = useCallback((data: WebSocketMessage) => {
    // Handle different types of WebSocket messages
    switch (data.type) {
      case 'execution_progress':
        if (data.data.progress !== undefined) {
          setExecutionProgress(data.data.progress);
        }
        if (data.data.current_step) {
          setCurrentStep(data.data.current_step);
        }
        break;
        
      case 'execution_update':
        setCurrentExecution(data.data.execution);
        break;
        
      case 'execution_completed':
        setExecutionProgress(100);
        setCurrentStep('Completed');
        setCurrentExecution(data.data.execution);
        break;
        
      case 'execution_failed':
        setCurrentStep('Failed');
        setCurrentExecution(data.data.execution);
        break;
        
      default:
        console.log('Unknown WebSocket message type:', data.type);
    }

    // Call custom message handler if provided
    if (onMessage) {
      onMessage(data);
    }
  }, [onMessage, setExecutionProgress, setCurrentStep, setCurrentExecution]);

  const connect = useCallback(() => {
    if (!wsManager.current) {
      wsManager.current = new WebSocketManager();
    }
    wsManager.current.connect(handleMessage, onError);
  }, [handleMessage, onError]);

  const disconnect = useCallback(() => {
    if (wsManager.current) {
      wsManager.current.disconnect();
    }
  }, []);

  const send = useCallback((data: any) => {
    if (wsManager.current) {
      wsManager.current.send(data);
    }
  }, []);

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  return {
    connect,
    disconnect,
    send,
    isConnected: wsManager.current !== null,
  };
}

// Hook specifically for execution updates
export function useExecutionWebSocket(executionId?: string) {
  const handleExecutionMessage = useCallback((data: WebSocketMessage) => {
    if (data.execution_id === executionId) {
      // Handle execution-specific updates
      switch (data.type) {
        case 'step_started':
          console.log(`Step started: ${data.data.step_name}`);
          break;
          
        case 'step_completed':
          console.log(`Step completed: ${data.data.step_name}`);
          break;
          
        case 'step_failed':
          console.error(`Step failed: ${data.data.step_name}`, data.data.error);
          break;
      }
    }
  }, [executionId]);

  return useWebSocket({
    onMessage: handleExecutionMessage,
    autoConnect: !!executionId,
  });
}